import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { ROLE_NAMES, defaultRange, rangeParams, useDebounced, useLoad, when } from "@/lib/staff";
import { INPUT, PageHead, Pager, RangePicker, Select, Table } from "@/components/staff/ui";
import { AuditChange, actionLabel } from "@/components/staff/bits";

const COLUMNS = [
  { key: "at", label: "When", render: (a) => <span className="whitespace-nowrap text-xs">{when(a.at)}</span> },
  { key: "actor", label: "Staff", render: (a) => <div className="min-w-0"><p className="text-xs truncate">{a.actor_email}</p><p className="text-[11px] text-neutral-500">{ROLE_NAMES[a.actor_role] || a.actor_role || "—"}</p></div> },
  { key: "action", label: "Action", render: (a) => <span className="text-sm text-neutral-100">{actionLabel(a.action)}</span> },
  { key: "target", label: "Account", render: (a) => (a.target_id ? <Link to={`/staff/students/${a.target_id}`} className="text-xs text-sky-300 hover:underline" data-testid={`audit-target-${a.audit_id}`}>{a.target_email || "Open"}</Link> : <span className="text-xs text-neutral-600">—</span>) },
  { key: "reason", label: "Reason", className: "max-w-[320px]", render: (a) => <span className="text-xs text-neutral-300">{a.reason || "—"}</span> },
  { key: "change", label: "Change", className: "max-w-[280px]", render: (a) => <AuditChange a={a} /> },
];

export default function AuditPage() {
  const [sp, setSp] = useSearchParams();
  const targetId = sp.get("target_id") || "";
  const actorId = sp.get("actor_id") || "";
  const [range, setRange] = useState(() => defaultRange("30d"));
  const [action, setAction] = useState("");
  const [text, setText] = useState("");
  const q = useDebounced(text.trim());
  const [page, setPage] = useState(1);
  const [actions, setActions] = useState([]);
  const params = useMemo(() => ({ ...rangeParams(range), action, q, target_id: targetId, actor_id: actorId, page }), [range, action, q, targetId, actorId, page]);
  const { data, error, loading, reload } = useLoad("/staff/audit", params);
  useEffect(() => { if (data?.actions) setActions(data.actions); }, [data]);
  const reset = (fn) => (v) => { fn(v); setPage(1); };
  return (
    <div data-testid="staff-audit">
      <PageHead title="Audit log" sub="Every staff action, who did it, when and why. Entries can't be edited or deleted by anyone, including the owner." />
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="audit-filters">
        <RangePicker value={range} onChange={reset(setRange)} testid="audit-range" />
        <Select value={action} onChange={reset(setAction)} options={[["", "All actions"], ...actions.map((a) => [a, actionLabel(a)])]} label="Action" testid="audit-action-filter" />
        <input value={text} onChange={(e) => { setText(e.target.value); setPage(1); }} placeholder="Search staff, account or reason" className={`${INPUT} w-72`} aria-label="Search audit log" data-testid="audit-search-input" />
        {(targetId || actorId) && (
          <button onClick={() => { setSp({}, { replace: true }); setPage(1); }} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-sky-400/15 text-sky-100 text-xs" data-testid="audit-account-filter-clear">
            One account only <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <Table testid="audit-table" columns={COLUMNS} rows={data?.items} rowKey={(a) => a.audit_id} loading={loading} error={error} onRetry={reload} empty="No staff activity in this range." />
      {data && <Pager page={page} pageSize={data.page_size} total={data.total} onPage={setPage} testid="audit-pager" />}
    </div>
  );
}
